import { Navigate, useParams } from 'react-router';
import PageShell from '@components/pages/PageShell';
import {
    FocusHero,
    FocusArticles,
    FocusDocuments,
    FocusGallery,
    FocusStories,
    HighlightedProjects,
    TakeItForASpin,
    Objectives,
    StatementBlock,
} from '@components/pages/FocusSections';
import { getFocusArea } from '@/data/pages';
import { getFocusContent } from '@/data/focusContent';
import Seo from '@components/Seo';
import JsonLd from '@components/JsonLd';
import { breadcrumbSchema } from '@/lib/schema';
import { PageSkeleton, useSiteLoading } from '@components/Skeleton';

/**
 * One template drives all five Focus Area pages — the designs are identical apart
 * from the hero copy, which comes from FOCUS_AREA_PAGES keyed by the URL slug,
 * and the programme's own content, which comes from FOCUS_CONTENT.
 */
export default function FocusArea() {
    const { slug } = useParams();
    const page = getFocusArea(slug);

    const loading = useSiteLoading();

    // The pools are empty until the feed lands, so "no such page" is not a
    // conclusion that can be drawn yet — drawing it sent every programme,
    // product and project link to the 404, and a redirect cannot be undone.
    if (!page) {
        if (loading) {
            return (
                <PageShell>
                    <div className="bg-white pb-14 pt-[120px] lg:pb-20 lg:pt-[160px]">
                        <PageSkeleton />
                    </div>
                </PageShell>
            );
        }
        return <Navigate to="/404" replace />;
    }

    const content = getFocusContent(page.slug);
    const area = `${page.titleLead} ${page.titleBold}`.trim();

    return (
        <PageShell>
            <JsonLd
                id="breadcrumb"
                data={breadcrumbSchema([
                    { name: 'Home', path: '/' },
                    { name: 'Focus Areas', path: '/#focus-areas' },
                    { name: area, path: `/focus-areas/${page.slug}` },
                ])}
            />
            <Seo title={area} description={page.intro} image={page.heroImage} canonical={`/focus-areas/${page.slug}`} />
            <FocusHero
                key={page.slug}
                titleLead={page.titleLead}
                titleBold={page.titleBold}
                subtitle={page.subtitle}
                intro={page.intro}
                heroImage={page.heroImage}
            />

            {/* Objectives lead — what the programme is for, before what it runs. */}
            <Objectives objectives={content.objectives} />
            <HighlightedProjects program={page.slug} />
            <TakeItForASpin video={content.video} />

            <FocusStories area={area} content={content} />
            <FocusArticles area={area} content={content} />
            <FocusGallery area={area} content={content} />
            <FocusDocuments area={area} content={content} />

            <StatementBlock dark={page.statementDark} grey={page.statementGrey} />
        </PageShell>
    );
}
